import { useQuery, UseQueryResult } from "react-query";
import CustomerApiService from "../../CustomerApiService";
import { MetadataViewerResponse } from "@cinesys/experience-ui/components";

/**
 * `cs_metadata_viewer` data fetching — the one genuinely new file this port needed (Cantemo has
 * no equivalent: its top-level wiring is Portal DOM-injection glue, `features/itemPanel.tsx`,
 * with nowhere to land in a standalone SPA — see `MetadataViewerPanel.tsx`'s header doc comment).
 * Follows this app's house `react-query` convention (`CLAUDE.md`).
 *
 * `GET /cs_metadata_viewer/metadata/:type/:entityId` 404s when the feature is disabled or
 * unconfigured (`apps/customer-api/src/routes/cs_metadata_viewer/index.ts`'s doc comment) rather
 * than carrying its own `enabled` field — `MetadataViewerPanel`'s `FeatureGate` reads that 404
 * off the query's `error`, same as every other baseline surface in this app.
 */
export function getItemMetadata(
  assetId: string
): Promise<MetadataViewerResponse> {
  return CustomerApiService.fetch<MetadataViewerResponse>(
    "GET",
    `/cs_metadata_viewer/metadata/item/${encodeURIComponent(assetId)}`
  );
}

export const cs_metadata_viewerQueryKeys = {
  item: (assetId: string) =>
    ["cs_metadata_viewer", "metadata", "item", assetId] as const,
};

export function useItemMetadata(
  assetId: string | undefined
): UseQueryResult<MetadataViewerResponse, Error> {
  return useQuery<MetadataViewerResponse, Error>(
    cs_metadata_viewerQueryKeys.item(assetId ?? ""),
    () => getItemMetadata(assetId as string),
    { enabled: !!assetId, staleTime: 30_000 }
  );
}
